import { formatFileSize } from '@/features/workspace-files/format';

/**
 * What the service will take as a task attachment (service spec 113 FR-011),
 * mirrored here so a file is refused the moment it is added rather than after
 * the task is created and the upload fails (spec 120 FR-002). The service's
 * own refusal stays authoritative; this is early feedback, not a second rule.
 */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

const ACCEPTED_TYPES: ReadonlySet<string> = new Set([
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/html',
  'text/xml',
  'application/json',
  'application/xml',
  'application/yaml',
  'application/x-yaml',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/zip',
  'application/octet-stream',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
]);

/**
 * Other names browsers give the types above, and the name the service takes.
 *
 * Chrome on Windows reports a `.zip` as `application/x-zip-compressed`, and a
 * `.csv` as `application/vnd.ms-excel` — the type the operating system's
 * registry holds, not the file's. Left as they come, a zip could not be attached
 * at all from Windows. They are the same files, so they are sent under the
 * service's name.
 */
const ALIASES: Readonly<Record<string, string>> = {
  'application/x-zip-compressed': 'application/zip',
  'application/x-zip': 'application/zip',
  'multipart/x-zip': 'application/zip',
  'application/vnd.ms-excel': 'text/csv',
  'text/x-markdown': 'text/markdown',
  'text/yaml': 'application/yaml',
  'text/x-yaml': 'application/yaml',
};

/**
 * `file` under the type the service takes for it: renamed when the browser
 * used another name for that type, and the same file when it did not, or when
 * only the extension could say (a `.xls` is not a CSV). Parameters such as
 * `; charset=utf-8` and letter case are ignored when matching.
 */
export function asAttachment(file: File): File {
  const type = file.type.split(';')[0]?.trim().toLowerCase() ?? '';
  const canonical = ALIASES[type];
  if (!canonical) return file;
  // A .xls is a spreadsheet, not a CSV: only the extension says which.
  if (canonical === 'text/csv' && !/\.csv$/i.test(file.name)) return file;
  return new File([file], file.name, {
    type: canonical,
    lastModified: file.lastModified,
  });
}

/**
 * Why `file` cannot be attached, or `undefined` if it can.
 *
 * A file whose type the browser could not tell is let through: it is sent as
 * `application/octet-stream`, which the service accepts. Only a type the
 * browser *does* know, and the service does not take, is refused here.
 */
export function refusalFor(
  file: Pick<File, 'size' | 'type'>,
): string | undefined {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `Larger than ${formatFileSize(MAX_ATTACHMENT_BYTES)} — too big to attach.`;
  }
  const type = file.type.split(';')[0]?.trim().toLowerCase() ?? '';
  if (type !== '' && !ACCEPTED_TYPES.has(type)) {
    return 'This kind of file cannot be attached.';
  }
  return undefined;
}
